import React, { useMemo, useState } from 'react';
import { useT } from '../i18n';
import { Icon, Modal, Ring, Spinner, useToast } from './ui';
import { addWaterMl, formatLiters, isAiEntry, macrosOf, setWaterGoal, WATER } from '../lib/services';
import { dateKey, isSameDay, weekDates } from '../lib/dates';
import { checkAndAwardBadges } from '../lib/badges';

export const MACRO_COLORS = { protein: 'var(--gm-protein)', carbs: 'var(--gm-carbs)', fat: 'var(--gm-fat)' };
export const DEFAULT_GOALS = { protein: 150, carbs: 250, fat: 65, calories: 2000 };

export function mealTypeLabel(t, type) {
  return { breakfast: t('breakfast'), lunch: t('lunch'), dinner: t('dinner'), snack: t('snack') }[type] || type || t('quickAdd');
}

export function mealIcon(entry) {
  if (!isAiEntry(entry)) return entry.source === 'barcode' ? 'qr_code_scanner' : 'bolt';
  return { breakfast: 'wb_sunny', lunch: 'lunch_dining', dinner: 'nightlight', snack: 'cookie' }[entry.meal_type] || 'restaurant';
}

export function PeriodTabs({ weekly, onChange }) {
  const t = useT();
  return (
    <div className="gm-segment">
      <button type="button" className={!weekly ? 'active' : ''} onClick={() => onChange(false)}>{t.upper(t('nutritionTabDaily'))}</button>
      <button type="button" className={weekly ? 'active' : ''} onClick={() => onChange(true)}>{t.upper(t('nutritionTabWeekly'))}</button>
    </div>
  );
}

export function CalorieFlameBar({ current, goal }) {
  const t = useT();
  const over = goal > 0 && current > goal;
  const reached = goal > 0 && current === goal;
  const progress = goal > 0 ? Math.min(1, current / goal) : 0;
  const fill = over ? 1 : progress;
  const goalMark = over ? goal / current : 1;
  let status = t('caloriesRemaining', Math.max(0, goal - current));
  let color = 'var(--gm-muted)';
  if (over) {
    status = t('caloriesOver', current - goal);
    color = 'var(--gm-over)';
  } else if (reached) {
    status = t('caloriesGoalReached');
    color = 'var(--gm-flame-b)';
  }
  return (
    <div className="gm-card">
      <div className="gm-row-between" style={{ alignItems: 'baseline' }}>
        <div className="gm-upper-sm" style={{ fontSize: 14 }}>{t.upper(t('calories'))}</div>
        <div>
          <span style={{ fontSize: 20, fontWeight: 800, color: over ? 'var(--gm-over)' : undefined }}>{current}</span>
          <span className="gm-muted"> / {goal} kcal</span>
        </div>
      </div>
      <div className="gm-flame-track">
        <div className="gm-flame-track__bar" />
        <div className={`gm-flame-track__fill${over ? ' over' : ''}`} style={{ width: `${fill * 100}%` }} />
        {over && <div className="gm-flame-track__goal" style={{ left: `calc(${goalMark * 100}% - 1px)` }} />}
        <div className="gm-flame-track__flame" style={{ left: `clamp(11px, ${fill * 100}%, calc(100% - 11px))` }}>🔥</div>
      </div>
      <div className="gm-small" style={{ color, fontWeight: 600 }}>{status}</div>
    </div>
  );
}

export function MacroRings({ totals, goals }) {
  const t = useT();
  return (
    <div className="gm-card">
      <div className="gm-rings">
        <Ring label={t('protein')} value={totals.protein_g} goal={goals.protein} color="var(--gm-protein)" />
        <Ring label={t('carbs')} value={totals.carbs_g} goal={goals.carbs} color="var(--gm-carbs)" />
        <Ring label={t('fat')} value={totals.fat_g} goal={goals.fat} color="var(--gm-fat)" />
      </div>
    </div>
  );
}

/* ───────── Water ───────── */
export function WaterGoalModal({ uid, goal, onClose }) {
  const t = useT();
  const [value, setValue] = useState(goal);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await setWaterGoal(uid, value);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={t('waterGoalDialogTitle')}
      footer={(
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose}>{t('cancel')}</button>
          <button type="button" className="gm-btn" onClick={save} disabled={busy}>{t('save')}</button>
        </>
      )}
    >
      <div style={{ textAlign: 'center', fontSize: 34, fontWeight: 800, color: 'var(--gm-water)' }}>{formatLiters(value, t.lang)}</div>
      <input type="range" min={WATER.min} max={WATER.max} step={WATER.step} value={value} onChange={(e) => setValue(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--gm-water)', marginTop: 12 }} />
      <p className="gm-muted gm-small" style={{ marginTop: 8 }}>{t('waterGoalHint')}</p>
    </Modal>
  );
}

export function WaterTrackerCard({ uid, ml, goal, readOnly }) {
  const t = useT();
  const toast = useToast();
  const [goalOpen, setGoalOpen] = useState(false);
  const fill = goal > 0 ? Math.min(1, ml / goal) : 0;
  const reached = ml >= goal;
  const add = (amount) => {
    let a = amount;
    if (a < 0 && ml + a < 0) a = -ml;
    if (!a) return;
    addWaterMl(uid, a)
      // Only on crossing the goal: a badge check reads every collection, too much per glass.
      .then(() => ml < goal && ml + a >= goal && checkAndAwardBadges(uid).catch(() => {}))
      .catch(() => toast.error(t('anErrorOccurred')));
  };
  return (
    <div className="gm-card" style={{ background: 'color-mix(in srgb, var(--gm-water) 7%, var(--gm-glass))' }}>
      <div className="gm-water">
        <div className="gm-glass"><div className="gm-glass__water" style={{ height: `${fill * 100}%` }} /></div>
        <div className="gm-grow">
          <div className="gm-row-between">
            <div className="gm-upper-sm" style={{ fontSize: 14 }}>{t.upper(t('waterTitle'))}</div>
            {!readOnly && (
              <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={() => setGoalOpen(true)} title={t('waterGoalDialogTitle')}>
                <Icon name="tune" />
              </button>
            )}
          </div>
          <div style={{ marginTop: 2 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--gm-water)' }}>{formatLiters(ml, t.lang)}</span>
            <span className="gm-muted"> / {formatLiters(goal, t.lang)}</span>
          </div>
          <div className="gm-small gm-muted">
            {reached ? t('waterGoalReached') : t('waterRemaining', formatLiters(goal - ml, t.lang))}
          </div>
          {!readOnly && (
            <div className="gm-row gm-wrap" style={{ marginTop: 10, gap: 6 }}>
              {WATER.quick.map((a) => (
                <button key={a} type="button" className="gm-water-btn" onClick={() => add(a)}>
                  <Icon name="water_drop" size={16} />+{a} ml
                </button>
              ))}
              <button type="button" className="gm-water-minus" disabled={ml <= 0} onClick={() => add(-200)} title={t('waterRemoveTooltip')}>
                <Icon name="remove" size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
      {goalOpen && <WaterGoalModal uid={uid} goal={goal} onClose={() => setGoalOpen(false)} />}
    </div>
  );
}

export function WeeklyWaterCard({ waterByDate, goal, today }) {
  const t = useT();
  const days = weekDates(today);
  const values = days.map((d) => waterByDate[dateKey(d)] || 0);
  const max = Math.max(goal, ...values, 1);
  const met = values.filter((v) => v >= goal).length;
  return (
    <div className="gm-card">
      <div className="gm-row-between" style={{ marginBottom: 12 }}>
        <div className="gm-upper-sm" style={{ fontSize: 14 }}>{t.upper(t('weeklyWaterTitle'))}</div>
        <span className="gm-pill" style={{ color: 'var(--gm-water)' }}>{t('waterDaysGoalMet', met)}</span>
      </div>
      <div className="gm-bars" style={{ height: 120 }}>
        <div className="gm-bars__target" style={{ bottom: `calc(${(goal / max) * 100}% * 0.72 + 22px)` }} />
        {days.map((d, i) => {
          const isFuture = d > today && !isSameDay(d, today);
          const v = values[i];
          return (
            <div key={dateKey(d)} className="gm-bars__col">
              <div className="gm-bars__val">{v ? formatLiters(v, t.lang).replace(' L', '') : ''}</div>
              <div
                className="gm-bars__bar"
                style={{
                  height: `${(v / max) * 72}%`,
                  background: isFuture ? 'color-mix(in srgb, var(--gm-water) 6%, transparent)' : v >= goal ? 'var(--gm-water)' : 'color-mix(in srgb, var(--gm-water) 55%, transparent)',
                }}
              />
              <div className={`gm-bars__lbl${isSameDay(d, today) ? ' today' : ''}`}>{t.dayShort(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][i])}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WeeklyCaloriesCard({ values, target, today }) {
  const t = useT();
  const days = weekDates(today);
  const max = Math.max(target || 0, ...values, 1);
  const avg = Math.round(values.reduce((a, b) => a + b, 0) / Math.max(1, values.filter((v) => v > 0).length || 1));
  return (
    <div className="gm-card">
      <div className="gm-row-between" style={{ marginBottom: 4 }}>
        <div className="gm-upper-sm" style={{ fontSize: 14 }}>{t.upper(t('calories'))}</div>
        <span className="gm-pill">{t('sevenDayAverageLabel')}: {avg} kcal</span>
      </div>
      {target ? <div className="gm-tiny gm-muted" style={{ marginBottom: 10 }}>{t('macroTargetsDailyCalorieGoalLine', target)}</div> : <div style={{ height: 10 }} />}
      <div className="gm-bars">
        {target > 0 && <div className="gm-bars__target" style={{ bottom: `calc(${(target / max) * 100}% * 0.72 + 22px)` }} />}
        {days.map((d, i) => (
          <div key={dateKey(d)} className="gm-bars__col">
            <div className="gm-bars__val">{values[i] || ''}</div>
            <div
              className="gm-bars__bar"
              style={{
                height: `${(values[i] / max) * 72}%`,
                background: target && values[i] > target ? 'linear-gradient(180deg, var(--gm-over), var(--gm-flame-b))' : 'linear-gradient(180deg, var(--gm-flame-a), var(--gm-flame-b))',
              }}
            />
            <div className={`gm-bars__lbl${isSameDay(d, today) ? ' today' : ''}`}>{t.dayShort(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][i])}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────── Meal entry card + detail ───────── */
function MacroPill({ name, grams, color }) {
  return (
    <span className="gm-macro-pill" style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }}>
      {name.charAt(0).toLocaleUpperCase()} {grams}g
    </span>
  );
}

export function MealCard({ entry, onOpen, onDelete }) {
  const t = useT();
  const ai = isAiEntry(entry);
  const title = ai ? mealTypeLabel(t, entry.meal_type) : entry.label || t('quickAdd');
  const foods = ai ? entry.items.map((i) => i.food_name).join(', ') : null;
  const tot = macrosOf(entry.totals);
  const created = entry.created_at ? new Date(entry.created_at) : null;
  const time = created && !Number.isNaN(created.getTime()) ? created.toLocaleTimeString(t.locale, { hour: '2-digit', minute: '2-digit' }) : null;
  return (
    <div className="gm-card gm-card--click gm-meal" onClick={onOpen}>
      <div className="gm-row">
        <span className="gm-meal__chip"><Icon name={mealIcon(entry)} />{t.upper(title)}</span>
        <span className="gm-grow" />
        {time && <span className="gm-small gm-muted">{time}</span>}
        {onDelete && (
          <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={(e) => { e.stopPropagation(); onDelete(); }} title={t('deleteEntry')}>
            <Icon name="delete" outline />
          </button>
        )}
      </div>
      {foods && <div className="gm-meal__foods">{foods}</div>}
      <div className="gm-row gm-wrap" style={{ marginTop: 10 }}>
        <span className="gm-meal__kcal">{tot.calories_kcal}<span className="gm-small gm-muted" style={{ fontWeight: 500 }}> kcal</span></span>
        <span className="gm-grow" />
        <MacroPill name={t('protein')} grams={tot.protein_g} color="var(--gm-protein)" />
        <MacroPill name={t('carbs')} grams={tot.carbs_g} color="var(--gm-carbs)" />
        <MacroPill name={t('fat')} grams={tot.fat_g} color="var(--gm-fat)" />
      </div>
    </div>
  );
}

export function MealDetailModal({ entry, dateStr, onClose }) {
  const t = useT();
  if (!entry) return null;
  const ai = isAiEntry(entry);
  const tot = macrosOf(entry.totals);
  return (
    <Modal open onClose={onClose} title={ai ? mealTypeLabel(t, entry.meal_type) : entry.label || t('quickAdd')} footer={<button type="button" className="gm-btn" onClick={onClose}>{t('ok')}</button>}>
      <p className="gm-small gm-muted" style={{ marginBottom: 12 }}>{dateStr}</p>
      {ai && (
        <div className="gm-stack-sm" style={{ marginBottom: 16 }}>
          {entry.items.map((i) => {
            const m = macrosOf(i.macros);
            return (
              <div key={i.id || i.food_name} className="gm-card" style={{ padding: 12 }}>
                <div className="gm-row-between">
                  <div className="gm-strong">{i.food_name}</div>
                  <div className="gm-strong">{m.calories_kcal} kcal</div>
                </div>
                <div className="gm-small gm-muted">{i.quantity}{i.quantity_estimated ? ' ~' : ''}</div>
                <div className="gm-tiny gm-muted" style={{ marginTop: 4 }}>
                  {t('protein')} {m.protein_g}g · {t('carbs')} {m.carbs_g}g · {t('fat')} {m.fat_g}g
                </div>
                {i.assumption_note && <div className="gm-tiny" style={{ marginTop: 6, fontStyle: 'italic' }}>{i.assumption_note}</div>}
              </div>
            );
          })}
        </div>
      )}
      <div className="gm-upper-sm" style={{ marginBottom: 6 }}>{t.upper(t('mealTotals'))}</div>
      <div className="gm-kv"><span>{t('calories')}</span><b>{tot.calories_kcal} kcal</b></div>
      <div className="gm-kv"><span>{t('protein')}</span><b>{tot.protein_g} g</b></div>
      <div className="gm-kv"><span>{t('carbs')}</span><b>{tot.carbs_g} g</b></div>
      <div className="gm-kv"><span>{t('fat')}</span><b>{tot.fat_g} g</b></div>
    </Modal>
  );
}

/** Groups nutrition day docs for the records list. */
export function useDayTotals(day) {
  return useMemo(() => macrosOf(day?.day_totals), [day]);
}

export function BusyButton({ busy, children, className = 'gm-btn', ...rest }) {
  return (
    <button type="button" className={className} disabled={busy || rest.disabled} {...rest}>
      {busy && <Spinner />}
      {children}
    </button>
  );
}
